// Carriers settings — PEXIFY-owned, provider-agnostic carrier catalog.
//
// One row per carrier (deduped across rate sources). The "Sources" column shows
// which active providers serve it (SERA / USPS-direct / ShipStation); a carrier
// with no active source is greyed (not shippable) but stays in the catalog —
// disconnect-safe. Per-service enable/disable feeds the rate-quote filter
// provider-agnostically. The catalog is seeded/refreshed from ShipStation.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, RefreshCw, X } from 'lucide-react'
import { API_BASE } from '../../../lib/api'
import { RESELLER_CHANNEL_NAMES } from '../../../lib/shippingProviders'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { useT } from '../../../i18n'
import {
  shippingProvidersI18n,
  type ShippingProvidersStrings,
} from '../../../i18n/admin/shipping-providers'
import type { CarrierServiceRow, ServiceGroup } from '../../../mocks/data/carrier-services'

type CarrierSummary = {
  carrier_id: string
  carrier_code: string
  label: string
  glyph: string
  glyph_bg: string
  glyph_fg: string
  enabled: boolean
  shippable: boolean
  sources: string[]
  services_enabled: number
  services_total: number
}

const base = `${API_BASE}/admin/shipping/carriers`

const SOURCE_LABEL: Record<string, string> = {
  ...RESELLER_CHANNEL_NAMES,
  shipstation: 'ShipStation',
  sera: 'SERA',
  usps_direct: 'USPS-direct',
  uniuni: 'UniUni',
}

// Rendered inside the Shipping providers hub, which owns the page title.
/** Channel id → the name the operator knows it by. */
const CHANNEL_LABEL: Record<string, string> = {
  ...RESELLER_CHANNEL_NAMES,
  shipstation: 'ShipStation',
  stamps: 'SERA',
  usps: 'USPS direct',
  ups: 'UPS direct',
}

type CarrierStrings = ShippingProvidersStrings['carriers']

export function CarriersSettings() {
  const t = useT(shippingProvidersI18n).carriers
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['shipping-carriers'],
    queryFn: async (): Promise<{ items: CarrierSummary[] }> => {
      const res = await fetch(base)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })

  const sync = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${base}/sync`, { method: 'POST' })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipping-carriers'] }),
  })

  const [servicesFor, setServicesFor] = useState<CarrierSummary | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-[var(--color-text-muted)]">
          {t.intro}
        </p>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (sync.isPending ? 'animate-spin' : '')} />
          {sync.isPending ? t.refreshing : t.refreshFromShipStation}
        </button>
      </div>

      <DataTableShell>
        <table>
          <thead>
            <tr>
              <th>{t.colCarrier}</th>
              <th>{t.colServices}</th>
              <th>{t.colSources}</th>
              <th className="text-right">{t.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="py-10 text-center text-[var(--color-text-muted)]">
                  {t.loading}
                </td>
              </tr>
            ) : (
              (data?.items ?? []).map((c) => (
                <CarrierRow key={c.carrier_id} carrier={c} t={t} onOpenServices={() => setServicesFor(c)} />
              ))
            )}
          </tbody>
        </table>
      </DataTableShell>

      {servicesFor && (
        <ShippingOptionsModal carrier={servicesFor} t={t} onClose={() => setServicesFor(null)} />
      )}
    </div>
  )
}

function CarrierRow({
  carrier,
  t,
  onOpenServices,
}: {
  carrier: CarrierSummary
  t: CarrierStrings
  onOpenServices: () => void
}) {
  const sourceLabel: Record<string, string> = { ...SOURCE_LABEL, usps_direct: t.sourceUspsDirect }
  const qc = useQueryClient()
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await fetch(`${base}/${carrier.carrier_id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabled }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipping-carriers'] }),
  })

  return (
    <tr className={'align-middle ' + (carrier.shippable ? '' : 'opacity-55')}>
      <td>
        <div className="flex items-center gap-3">
          <Toggle
            checked={carrier.enabled}
            disabled={toggle.isPending}
            onChange={(v) => toggle.mutate(v)}
          />
          <span
            className={`flex h-7 w-9 shrink-0 items-center justify-center rounded text-[10px] font-bold ${carrier.glyph_bg} ${carrier.glyph_fg}`}
          >
            {carrier.glyph}
          </span>
          <div>
            <span className="font-medium text-[var(--color-text)]">{carrier.label}</span>
            {!carrier.shippable && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                {t.noActiveSource}
              </span>
            )}
          </div>
        </div>
      </td>
      <td>
        <button
          type="button"
          onClick={onOpenServices}
          className="font-medium text-indigo-600 hover:underline"
        >
          {t.servicesCount(carrier.services_enabled, carrier.services_total)}
        </button>
      </td>
      <td className="py-1.5">
        <div className="flex flex-wrap gap-1">
          {carrier.sources.length === 0 ? (
            <span className="text-xs text-[var(--color-text-muted)]">—</span>
          ) : (
            carrier.sources.map((s) => (
              <span
                key={s}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
              >
                {sourceLabel[s] ?? s}
              </span>
            ))
          )}
        </div>
      </td>
      <td className="text-right">
        <button
          type="button"
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label={t.carrierActions}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ' +
        (checked ? 'bg-brand-500' : 'bg-slate-300')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

function ShippingOptionsModal({
  carrier,
  t,
  onClose,
}: {
  carrier: CarrierSummary
  t: CarrierStrings
  onClose: () => void
}) {
  const channelLabel: Record<string, string> = {
    ...CHANNEL_LABEL,
    usps: t.channelUspsDirect,
    ups: t.channelUpsDirect,
  }
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string> | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['shipping-carrier-services', carrier.carrier_id],
    queryFn: async (): Promise<{ services: CarrierServiceRow[] }> => {
      const res = await fetch(`${base}/${carrier.carrier_id}/services`)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })

  const services = data?.services ?? []
  const working = selected ?? new Set(services.filter((s) => s.enabled).map((s) => s.code))

  const toggleOne = (code: string) => {
    const next = new Set(working)
    if (next.has(code)) next.delete(code)
    else next.add(code)
    setSelected(next)
  }

  const allSelected = services.length > 0 && working.size === services.length
  const selectAll = () =>
    setSelected(allSelected ? new Set() : new Set(services.map((s) => s.code)))

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${base}/${carrier.carrier_id}/services`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabled_codes: Array.from(working) }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shipping-carriers'] })
      void qc.invalidateQueries({ queryKey: ['shipping-carrier-services', carrier.carrier_id] })
      onClose()
    },
  })

  const groups: ServiceGroup[] = ['domestic', 'international']

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <h2 className="text-base font-semibold text-slate-900">
            {t.modalTitle(carrier.label)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label={t.close}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <p className="border-b border-slate-100 px-5 py-2.5 text-xs text-slate-500">
          {t.modalHint}
        </p>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">{t.loading}</div>
          ) : (
            groups.map((g) => {
              const rows = services.filter((s) => s.group === g)
              if (rows.length === 0) return null
              return (
                <div key={g}>
                  <div className="bg-sky-50/60 px-5 py-2 text-sm font-semibold text-slate-700">
                    {t.groups[g]}
                  </div>
                  {rows.map((s) => (
                    <label
                      key={s.code}
                      className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-5 py-2.5 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={working.has(s.code)}
                        onChange={() => toggleOne(s.code)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
                      />
                      <span className="min-w-0 flex-1 text-slate-700">{s.name}</span>
                      {/* Which channels answer with this service; a switch hides
                          it in all of them. Empty until a rate quote names it. */}
                      {(s.sources ?? []).length > 0 && (
                        <span className="shrink-0 text-[11px] text-slate-400">
                          {(s.sources ?? []).map((src) => channelLabel[src] ?? src).join(' · ')}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )
            })
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={selectAll}
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            {allSelected ? t.deselectAll : t.selectAll}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {save.isPending ? t.saving : t.save}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
